import { useQuery } from "@tanstack/react-query";
import {
  ArrowLeft,
  ArrowRight,
  Copy,
  Download,
  ExternalLink,
  Filter,
  RefreshCw,
  Search,
  X,
} from "lucide-react";
import { useState, type FormEvent } from "react";
import { useSearchParams } from "react-router-dom";
import {
  api,
  type AdminAttempt,
  type AdminJob,
  type AdminJobDetail,
  type AdminJobsResponse,
  type NodeType,
} from "../api";
import { EmptyState, ErrorNotice, LoadingRows, Status, formatTime } from "../components";

const statuses = ["", "QUEUED", "RUNNING", "CANCEL_REQUESTED", "SUCCEEDED", "FAILED", "CANCELLED"];

type Filters = {
  search: string;
  status: string;
  outputFormat: string;
  nodeType: string;
  executionDevice: string;
  createdFrom: string;
  createdTo: string;
  limit: string;
};

const emptyFilters: Filters = {
  search: "",
  status: "",
  outputFormat: "",
  nodeType: "",
  executionDevice: "",
  createdFrom: "",
  createdTo: "",
  limit: "25",
};

export function JobsPage() {
  const [searchParams] = useSearchParams();
  const initialFilters = { ...emptyFilters, search: searchParams.get("search") ?? "" };
  const [draft, setDraft] = useState<Filters>(initialFilters);
  const [filters, setFilters] = useState<Filters>(initialFilters);
  const [cursors, setCursors] = useState<(string | null)[]>([null]);
  const [pageIndex, setPageIndex] = useState(0);
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);
  const cursor = cursors[pageIndex];
  const jobs = useQuery({
    queryKey: ["admin-jobs", filters, cursor],
    queryFn: () => api<AdminJobsResponse>(buildJobsPath(filters, cursor)),
    refetchInterval: 10_000,
  });
  const nodeTypes = useQuery({ queryKey: ["node-types"], queryFn: () => api<NodeType[]>("/admin/v1/node-types") });
  const detail = useQuery({
    queryKey: ["admin-job", selectedJobId],
    queryFn: () => api<AdminJobDetail>(`/admin/v1/jobs/${selectedJobId}`),
    enabled: selectedJobId !== null,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      if (status === "SUCCEEDED") return 30 * 60_000;
      if (status === "FAILED" || status === "CANCELLED") return false;
      return selectedJobId ? 5_000 : false;
    },
  });

  function applyFilters(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setFilters(draft);
    setCursors([null]);
    setPageIndex(0);
    setSelectedJobId(null);
  }

  function resetFilters() {
    setDraft(emptyFilters);
    setFilters(emptyFilters);
    setCursors([null]);
    setPageIndex(0);
    setSelectedJobId(null);
  }

  function nextPage() {
    const next = jobs.data?.next_cursor;
    if (!next) return;
    setCursors((current) => [...current.slice(0, pageIndex + 1), next]);
    setPageIndex((current) => current + 1);
    setSelectedJobId(null);
  }

  function previousPage() {
    if (pageIndex === 0) return;
    setPageIndex((current) => current - 1);
    setSelectedJobId(null);
  }

  return <div className="page page--jobs">
    <header className="page-heading jobs-heading">
      <div><p className="eyebrow">OPERATIONS / JOBS</p><h1>任务中心</h1><p>按业务标识定位任务，追踪每次执行，并直接获取成功结果。</p></div>
      <button className="button button--quiet" type="button" onClick={() => jobs.refetch()} disabled={jobs.isFetching}><RefreshCw size={15} className={jobs.isFetching ? "spin" : ""} />刷新</button>
    </header>

    <form className="job-filters" onSubmit={applyFilters}>
      <label className="job-filter job-filter--search"><span>业务标识或任务 ID</span><div><Search size={16} /><input value={draft.search} onChange={(event) => setDraft({ ...draft, search: event.target.value })} placeholder="输入 external_id 或完整 job_id" /></div></label>
      <label className="job-filter"><span>状态</span><select value={draft.status} onChange={(event) => setDraft({ ...draft, status: event.target.value })}>{statuses.map((value) => <option value={value} key={value}>{value || "全部状态"}</option>)}</select></label>
      <label className="job-filter"><span>格式</span><select value={draft.outputFormat} onChange={(event) => setDraft({ ...draft, outputFormat: event.target.value })}><option value="">全部格式</option><option value="mp3">MP3</option><option value="wav">WAV</option></select></label>
      <label className="job-filter"><span>节点类型</span><select value={draft.nodeType} onChange={(event) => setDraft({ ...draft, nodeType: event.target.value })}><option value="">全部类型</option>{nodeTypes.data?.map((type) => <option key={type.id} value={type.id}>{type.display_name}</option>)}</select></label>
      <label className="job-filter"><span>执行设备</span><select value={draft.executionDevice} onChange={(event) => setDraft({ ...draft, executionDevice: event.target.value })}><option value="">全部设备</option><option value="cpu">CPU</option><option value="cuda">CUDA</option></select></label>
      <label className="job-filter"><span>开始时间</span><input type="datetime-local" value={draft.createdFrom} onChange={(event) => setDraft({ ...draft, createdFrom: event.target.value })} /></label>
      <label className="job-filter"><span>结束时间</span><input type="datetime-local" value={draft.createdTo} onChange={(event) => setDraft({ ...draft, createdTo: event.target.value })} /></label>
      <label className="job-filter job-filter--size"><span>每页</span><select value={draft.limit} onChange={(event) => setDraft({ ...draft, limit: event.target.value })}><option value="20">20</option><option value="25">25</option><option value="50">50</option><option value="100">100</option></select></label>
      <div className="job-filter-actions"><button type="button" className="button button--quiet" onClick={resetFilters}>清除</button><button type="submit" className="button button--primary"><Filter size={15} />查询</button></div>
    </form>

    {jobs.error && <ErrorNotice error={jobs.error} />}
    <div className={`jobs-workspace ${selectedJobId ? "jobs-workspace--detail" : ""}`}>
      <section className="jobs-list" aria-label="任务列表">
        <div className="jobs-list__meta"><span>第 {pageIndex + 1} 页</span><span>{jobs.data ? `本页 ${jobs.data.items.length} 条` : "正在读取"}</span><span className="live-mark"><i />10 秒自动刷新</span></div>
        {jobs.isPending ? <LoadingRows columns={5} /> : jobs.data?.items.length ? <div className="table-wrap"><table className="responsive-table jobs-table"><thead><tr><th>任务</th><th>状态</th><th>处理设置</th><th>执行</th><th>创建时间</th><th aria-label="操作" /></tr></thead><tbody>{jobs.data.items.map((job) => <JobRow key={job.job_id} job={job} selected={job.job_id === selectedJobId} onSelect={() => setSelectedJobId(job.job_id)} />)}</tbody></table></div> : <EmptyState title="没有符合条件的任务">调整筛选条件，或等待业务提交新的转码任务。</EmptyState>}
        <footer className="pagination"><button className="button button--quiet" type="button" disabled={pageIndex === 0 || jobs.isFetching} onClick={previousPage}><ArrowLeft size={15} />上一页</button><span>{pageIndex + 1}</span><button className="button button--quiet" type="button" disabled={!jobs.data?.next_cursor || jobs.isFetching} onClick={nextPage}>下一页<ArrowRight size={15} /></button></footer>
      </section>
      {selectedJobId && <JobDetailPanel detail={detail.data} pending={detail.isPending} error={detail.error} onClose={() => setSelectedJobId(null)} />}
    </div>
  </div>;
}

function JobRow({ job, selected, onSelect }: { job: AdminJob; selected: boolean; onSelect: () => void }) {
  return <tr className={selected ? "jobs-table__selected" : undefined}>
    <td data-label="任务"><button className="job-identity" type="button" onClick={onSelect}><strong>{job.external_id}</strong><small>{compactId(job.job_id)}</small></button></td>
    <td data-label="状态"><Status value={job.status} />{job.error_code && <small className="critical-text">{job.error_code}</small>}</td>
    <td data-label="处理设置"><strong>{formatTargetDuration(job.duration_ms)} · {job.output_format.toUpperCase()}</strong><small>{job.node_type} · {job.server_denoise_enabled ? "服务端 DSP" : "原声"} · {job.mp3_bitrate_kbps} kbps</small></td>
    <td data-label="执行"><strong>{formatExecutionDevice(job.execution_device)} · {job.attempt_count} / {job.max_attempts}</strong><small>设备 · 已创建 / 最大尝试</small></td>
    <td data-label="创建时间">{formatTime(job.created_at)}<small>更新 {formatTime(job.updated_at)}</small></td>
    <td className="responsive-table__actions"><button className="button button--small" type="button" onClick={onSelect}>详情<ArrowRight size={14} /></button></td>
  </tr>;
}

function JobDetailPanel({ detail, pending, error, onClose }: { detail: AdminJobDetail | undefined; pending: boolean; error: unknown; onClose: () => void }) {
  return <aside className="job-detail" aria-label="任务详情">
    <header className="job-detail__header"><div><p className="eyebrow">JOB INSPECTOR</p><h2>{detail?.external_id ?? "任务详情"}</h2></div><button type="button" className="icon-button" onClick={onClose} aria-label="关闭详情"><X size={19} /></button></header>
    {error ? <ErrorNotice error={error} /> : pending || !detail ? <LoadingRows columns={2} /> : <>
      <div className="job-detail__status"><Status value={detail.status} /><span>更新于 {formatTime(detail.updated_at)}</span></div>
      {(detail.error_code || detail.error_message) && <section className="job-failure"><strong>{detail.error_code ?? "TASK_FAILED"}</strong><p>{detail.error_message ?? "任务执行失败，但没有上报详细原因。"}</p></section>}
      {detail.result && <ResultSection detail={detail} />}
      <section className="job-detail__section"><div className="job-detail__section-title"><h3>执行过程</h3><span>{detail.attempts.length} 次 attempt</span></div>{detail.attempts.length ? <div className="attempt-timeline">{detail.attempts.map((attempt) => <AttemptItem key={attempt.attempt_id} attempt={attempt} current={attempt.attempt_id === detail.current_attempt_id} />)}</div> : <p className="job-detail__empty">任务尚未被节点领取。</p>}</section>
      <section className="job-detail__section"><div className="job-detail__section-title"><h3>任务配置</h3></div><dl className="job-facts"><Fact label="节点类型" value={detail.node_type} /><Fact label="执行设备" value={formatExecutionDevice(detail.execution_device)} /><Fact label="推理后端" value={detail.inference_backend ?? "未执行 AI 推理"} />{detail.gpu_uuid && <Fact label="GPU UUID" value={detail.gpu_uuid} />}{detail.model_release_id && <Fact label="模型版本 ID" value={detail.model_release_id} />}<Fact label="目标时长" value={formatTargetDuration(detail.duration_ms)} /><Fact label="输出" value={`${detail.output_format.toUpperCase()} · ${detail.mp3_bitrate_kbps} kbps`} /><Fact label="音频处理" value={detail.server_denoise_enabled ? (detail.node_type === "ai-v1" ? "AI 降噪" : "服务端 DSP") : "原声模式"} />{detail.server_denoise_enabled && detail.node_type === "normal-v1" && <><Fact label="降噪等级" value={String(detail.dsp_config.ns_level)} /><Fact label="目标响度" value={`${detail.dsp_config.target_lufs} LUFS`} /><Fact label="最大补偿增益" value={`${detail.dsp_config.max_gain_db} dB`} /><Fact label="Limiter" value={detail.dsp_config.limiter_threshold.toFixed(2)} /></>}<Fact label="变调比例" value={detail.pitch_ratio.toFixed(2)} /><Fact label="保留时间" value={detail.retention_days === 0 ? "永久" : `${detail.retention_days} 天`} /><Fact label="Pipeline" value={detail.pipeline_version} /></dl></section>
      <section className="job-detail__section"><div className="job-detail__section-title"><h3>请求信息</h3><button type="button" className="icon-button" onClick={() => navigator.clipboard.writeText(detail.job_id)} title="复制任务 ID"><Copy size={15} /></button></div><dl className="job-facts job-facts--stack"><Fact label="Job ID" value={detail.job_id} /><Fact label="输入 URL（脱敏）" value={detail.input_url} /><Fact label="回调 URL（脱敏）" value={detail.callback_url ?? "未配置"} /><Fact label="创建时间" value={formatTime(detail.created_at)} /><Fact label="完成时间" value={formatTime(detail.finished_at)} /></dl></section>
    </>}
  </aside>;
}

function ResultSection({ detail }: { detail: AdminJobDetail }) {
  const result = detail.result!;
  return <section className="job-result">
    <div><p className="eyebrow">OUTPUTS</p><h3>{detail.output_format.toUpperCase()} 转码结果</h3><p>{result.cleanup_status}{result.result_expires_at ? ` · 保留至 ${formatTime(result.result_expires_at)}` : " · 永久保留"}</p></div>
    <ResultArtifact label="原音" url={result.download_url} expiresAt={result.download_url_expires_at} error={result.download_error} objectKey={result.object_key} sizeBytes={result.size_bytes} format={detail.output_format} primary />
    {detail.pitch_ratio !== 1 && <ResultArtifact label={`变调音 · ${detail.pitch_ratio.toFixed(2)}×`} url={result.pitch_download_url} expiresAt={result.pitch_download_url_expires_at} error={result.pitch_download_error} objectKey={result.pitch_object_key} sizeBytes={result.pitch_size_bytes} format={detail.output_format} />}
  </section>;
}

function ResultArtifact({ label, url, expiresAt, error, objectKey, sizeBytes, format, primary = false }: { label: string; url: string | null; expiresAt: string | null; error: string | null; objectKey: string | null; sizeBytes: number | null; format: string; primary?: boolean }) {
  return <article className="job-result__artifact"><header><div><strong>{label}</strong><small>{formatBytes(sizeBytes)}</small></div>{url && <a className={`button button--small ${primary ? "button--primary" : ""}`} href={url} target="_blank" rel="noreferrer"><Download size={15} />打开音频<ExternalLink size={12} /></a>}</header>{url ? <>{format === "mp3" && <audio controls preload="none" src={url}>当前浏览器不支持音频播放。</audio>}<small>{expiresAt ? `临时链接有效至 ${formatTime(expiresAt)}` : "公开读链接，无签名有效期"}</small></> : <p className="job-result__error">{error ?? "结果链接当前不可用"}</p>}{objectKey && <code title={objectKey}>{objectKey}</code>}</article>;
}

function AttemptItem({ attempt, current }: { attempt: AdminAttempt; current: boolean }) {
  const metrics = visibleMetrics(attempt.metrics);
  return <article className={`attempt-item ${current ? "attempt-item--current" : ""}`}>
    <span className="attempt-item__rail" aria-hidden="true" />
    <header><div><strong>Attempt {attempt.attempt_no}</strong>{current && <em>当前</em>}</div><Status value={attempt.status} /></header>
    <p className="attempt-item__node">{attempt.node_name ?? "尚未分配节点"}{attempt.pool_id ? ` · ${attempt.pool_id}` : ""} · {formatExecutionDevice(attempt.execution_device)}{attempt.inference_backend ? ` · ${attempt.inference_backend}` : ""}</p>
    <div className="attempt-item__times"><span>开始 {formatTime(attempt.started_at ?? attempt.created_at)}</span><span>结束 {formatTime(attempt.finished_at)}</span></div>
    {(attempt.error_code || attempt.error_message) && <div className="attempt-item__error"><strong>{attempt.error_code ?? "ATTEMPT_FAILED"}</strong><span>{attempt.error_message}</span></div>}
    {metrics.length > 0 && <dl>{metrics.map(([label, value]) => <div key={label}><dt>{label}</dt><dd>{value}</dd></div>)}</dl>}
  </article>;
}

function Fact({ label, value }: { label: string; value: string }) {
  return <div><dt>{label}</dt><dd title={value}>{value}</dd></div>;
}

function buildJobsPath(filters: Filters, cursor: string | null) {
  const params = new URLSearchParams({ limit: filters.limit });
  if (filters.search.trim()) params.set("search", filters.search.trim());
  if (filters.status) params.set("status", filters.status);
  if (filters.outputFormat) params.set("output_format", filters.outputFormat);
  if (filters.nodeType) params.set("node_type", filters.nodeType);
  if (filters.executionDevice) params.set("execution_device", filters.executionDevice);
  if (filters.createdFrom) params.set("created_from", new Date(filters.createdFrom).toISOString());
  if (filters.createdTo) params.set("created_to", new Date(filters.createdTo).toISOString());
  if (cursor) params.set("cursor", cursor);
  return `/admin/v1/jobs?${params.toString()}`;
}

function visibleMetrics(metrics: Record<string, unknown>) {
  const definitions: Array<[string, string, (value: number) => string]> = [
    ["agent_elapsed_ms", "总耗时", formatMilliseconds],
    ["worker_elapsed_ms", "DSP 耗时", formatMilliseconds],
    ["decoded_duration_ms", "实际音频时长", formatDuration],
    ["output_duration_ms", "输出音频时长", formatDuration],
    ["input_size_bytes", "输入", formatBytes],
    ["output_size_bytes", "输出", formatBytes],
  ];
  const values = definitions.flatMap(([key, label, formatter]) => typeof metrics[key] === "number" ? [[label, formatter(metrics[key] as number)] as [string, string]] : []);
  if (typeof metrics.input_cache_hit === "boolean") values.push(["RAW 缓存", metrics.input_cache_hit ? "命中" : "未命中"]);
  if (typeof metrics.duration_compressed === "boolean") values.push(["时长压缩", metrics.duration_compressed ? "已压缩" : "未压缩"]);
  return values;
}

function compactId(value: string) {
  return `${value.slice(0, 8)}…${value.slice(-6)}`;
}

function formatDuration(durationMs: number) {
  const seconds = Math.round(durationMs / 1000);
  if (seconds >= 3600) return `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m`;
  if (seconds >= 60) return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
  return `${seconds}s`;
}

function formatTargetDuration(durationMs: number | null) {
  return durationMs == null ? "按源音频时长" : formatDuration(durationMs);
}

function formatExecutionDevice(device: "cpu" | "cuda" | null) {
  if (device === "cuda") return "CUDA GPU";
  if (device === "cpu") return "CPU";
  return "尚未处理";
}

function formatMilliseconds(value: number) {
  return value >= 1000 ? `${(value / 1000).toFixed(2)} s` : `${Math.round(value)} ms`;
}

function formatBytes(value: number | null) {
  if (value == null) return "大小未知";
  if (value >= 1024 ** 3) return `${(value / 1024 ** 3).toFixed(2)} GB`;
  if (value >= 1024 ** 2) return `${(value / 1024 ** 2).toFixed(2)} MB`;
  if (value >= 1024) return `${(value / 1024).toFixed(1)} KB`;
  return `${value} B`;
}
